import { type FormEvent, type ReactNode, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { errorMessage } from '../api/client';
import { ContinueAsGuest, OrDivider } from '../components/ContinueAsGuest';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useSession } from '../stores/session';

/** Only follow redirects to paths on this site. */
function safeNext(next: string | null): string {
  return next?.startsWith('/') && !next.startsWith('//') ? next : '/';
}

function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <div className="dotted-backdrop relative flex min-h-full items-center justify-center overflow-hidden px-4 py-12">
      <div aria-hidden className="pointer-events-none absolute inset-0 hidden md:block">
        <div className="absolute top-[14%] left-[12%] size-36 -rotate-6 rounded-sm bg-[#fff176] shadow-lg" />
        <div className="absolute bottom-[16%] left-[18%] size-28 rotate-3 rounded-sm bg-[#b3e5fc] shadow-lg" />
        <div className="absolute top-[22%] right-[14%] size-32 rotate-6 rounded-sm bg-[#f8bbd0] shadow-lg" />
        <div className="absolute right-[20%] bottom-[18%] size-24 -rotate-3 rounded-sm bg-[#c5e1a5] shadow-lg" />
      </div>
      <div className="relative w-full max-w-sm rounded-2xl border border-slate-200 bg-white/95 p-8 shadow-float backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
        <Logo className="mb-6" />
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-1 mb-6 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

export function LoginPage() {
  const user = useSession((s) => s.user);
  const config = useSession((s) => s.config);
  const login = useSession((s) => s.login);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = safeNext(params.get('next'));

  if (user) return <Navigate to={next} replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(username, password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to your boards">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Input
          label="Username"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
          autoFocus
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && (
          <p
            role="alert"
            className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300"
          >
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" size="lg" loading={busy} className="mt-2">
          Sign in
        </Button>
      </form>
      {config?.allowGuests !== false && <OrDivider />}
      <ContinueAsGuest onDone={() => navigate(next, { replace: true })} />
      {config?.allowRegistration !== false && (
        <p className="mt-6 text-center text-sm text-slate-500">
          New here?{' '}
          <Link
            to={`/register${params.size ? `?${params}` : ''}`}
            className="font-medium text-brand-600 hover:underline dark:text-brand-400"
          >
            Create an account
          </Link>
        </p>
      )}
    </AuthLayout>
  );
}

export function RegisterPage() {
  const user = useSession((s) => s.user);
  const config = useSession((s) => s.config);
  const register = useSession((s) => s.register);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = safeNext(params.get('next'));

  if (user) return <Navigate to={next} replace />;
  if (config?.allowRegistration === false) {
    return (
      <AuthLayout
        title="Registration is closed"
        subtitle="Ask the person who runs this DoriavBoard for an account."
      >
        <Link to="/login" className="text-sm font-medium text-brand-600 hover:underline">
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await register({ name, username, password });
      navigate(next, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Create your account" subtitle="Start whiteboarding with your team">
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Input
          label="Name"
          autoComplete="name"
          required
          autoFocus
          maxLength={60}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Input
          label="Username"
          hint="For signing in. Letters, digits, dots, dashes and underscores."
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
          minLength={3}
          maxLength={30}
          pattern="[A-Za-z0-9._\-]+"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <Input
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          hint="At least 8 characters"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && (
          <p
            role="alert"
            className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300"
          >
            {error}
          </p>
        )}
        <Button type="submit" variant="primary" size="lg" loading={busy} className="mt-2">
          Create account
        </Button>
      </form>
      {config?.allowGuests !== false && <OrDivider />}
      <ContinueAsGuest onDone={() => navigate(next, { replace: true })} />
      <p className="mt-6 text-center text-sm text-slate-500">
        Already have an account?{' '}
        <Link
          to={`/login${params.size ? `?${params}` : ''}`}
          className="font-medium text-brand-600 hover:underline dark:text-brand-400"
        >
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
